import { useCallback, useMemo, useReducer } from 'react';
import { NoteFormValues } from 'modules/notes/notePopup/noteEdit/utils';
import { Client, StatusesItem } from 'store/clients/clientsSlice';

import NoteModal from './noteModal';

export type NoteModalConfig = {
  status: StatusesItem['id'];
  clientId: string;
  clientName: string;
  prependedClientId: string;
  note: Partial<NoteFormValues>;
};

type StatusSelectionNoteData = {
  statusInfo?: {
    status?: {
      id?: StatusesItem['id'];
    };
  };
  client?: Partial<Client> & {
    id?: string | number;
    name?: string;
    prependedId?: string | number;
  };
};

type NoteModalAction = { type: 'OPEN_MODAL'; payload: NoteModalConfig } | { type: 'CLOSE_MODAL' };

const noteConfigReducer = (
  state: NoteModalConfig | null,
  action: NoteModalAction
): NoteModalConfig | null => {
  switch (action.type) {
    case 'OPEN_MODAL':
      return { ...state, ...action.payload };
    case 'CLOSE_MODAL':
      return null;
    default:
      return state;
  }
};

export const getStatusSelectionNoteModalConfig = (
  data: StatusSelectionNoteData | undefined,
  statusesList: StatusesItem[] | undefined
): NoteModalConfig | null => {
  const selectedStatusId = data?.statusInfo?.status?.id;
  const client = data?.client;
  const noteConfig = statusesList?.find((status) => status.id === selectedStatusId)?.noteConfig;

  if (
    !selectedStatusId ||
    !client?.id ||
    !noteConfig?.noteTriggeredOnStatusSelection ||
    selectedStatusId === client.status?.id
  ) {
    return null;
  }

  const clientName = client.name ?? [client.firstName, client.lastName].filter(Boolean).join(' ');

  return {
    status: selectedStatusId,
    clientId: client.id.toString(),
    clientName,
    prependedClientId: client.prependedId?.toString() ?? '',
    note: {
      noteType: noteConfig.preselectedNoteType?.id ?? ''
    }
  };
};

export const useNoteModal = () => {
  const [config, dispatchNoteConfig] = useReducer(noteConfigReducer, null);

  const handleOpen = useCallback((payload: NoteModalConfig) => {
    dispatchNoteConfig({ type: 'OPEN_MODAL', payload });
  }, []);

  const handleClose = useCallback(() => {
    dispatchNoteConfig({ type: 'CLOSE_MODAL' });
  }, []);

  const open = config !== null;

  const noteModalElement = useMemo(() => {
    if (!config) return null;
    return (
      <NoteModal
        status={config.status}
        open={open}
        clientId={config.clientId}
        clientName={config.clientName}
        prependedClientId={config.prependedClientId}
        handleClose={handleClose}
        note={config.note ?? undefined}
        onSubmitSuccess={handleClose}
        onDeleteSuccess={handleClose}
      />
    );
  }, [config, handleClose, open]);

  return {
    config,
    open,
    handleOpen,
    handleClose,
    note: config?.note ?? null,
    noteModalElement
  };
};
